import * as React from "react";
import Box from "@mui/material/Box";
import {
  Button,
  Container,
  Grid,
  IconButton,
  Stack,
  Tooltip,
} from "@mui/material";
import { CustomTextView } from "../../component/CustomTextView";
import { CustomCardView } from "../../component/CustomCardView";
import {
  COLOR_LESS_BLACK,
  COLOR_LIGHT_GRAY,
  COLOR_MAIN,
} from "../../../data/colors/Colors";
import {
  DownloadRounded,
  LockRounded,
  Logout,
  SortRounded,
} from "@mui/icons-material";
import SharedPreferences from "../../../core/utils/SharedPreferences";
import { STORAGE_KEYS } from "../../../data/constant/Constants";
import { HomeViewModel } from "./HomeViewModel";
import {
  provideGenerateXmlFileUseCase,
  provideGetAllTrucksUseCase,
} from "../../di/UseCasesModule";
import { LoadingView } from "../../component/LoadingView";
import { EmptyView } from "../../component/EmptyView";
import { STRINGS_LOAD_MORE } from "../../../data/strings/Strings";
import { TrucksListItemView } from "./TrucksListItemView";
import { CustomPopoverView } from "../../component/CustomPopoverView";
import { FilterTruckView } from "./FilterTruckView";
import { CustomButtonView } from "../../component/CustomButtonView";
import { ChangePasswordView } from "./changePassword/ChangePasswordView";

export function HomeView() {
  const viewModel = HomeViewModel(
    provideGetAllTrucksUseCase(),
    provideGenerateXmlFileUseCase()
  );

  const [popoverViewType, setPopoverViewType] = React.useState(
    PopoverViewType.FILTER_TRUCKS
  );
  const [anchorElPosition, setAnchorElPosition] = React.useState<{
    top: number;
    left: number;
  } | null>(null);

  const trucksListRef = React.useRef<null | HTMLDivElement>(null);

  React.useEffect(() => {
    if (viewModel.trucks?.length != 0) {
      if (trucksListRef.current)
        trucksListRef.current.scrollTop = trucksListRef.current.scrollHeight;
    }
  }, [viewModel.trucks]);
  return (
    <Box>
      <CustomPopoverView
        onClose={() => {
          setAnchorElPosition(null);
        }}
        anchorElPosition={anchorElPosition}
        layout={
          <>
            {popoverViewType == PopoverViewType.FILTER_TRUCKS ? (
              <FilterTruckView
                onClose={() => {
                  setAnchorElPosition(null);
                }}
                onGenerateXMLFileClicked={() => {
                  if (!viewModel.truckFilteringModel?.name) return;
                  if (!viewModel.truckFilteringModel?.rangeDate) return;
                  viewModel.generateXmlFile(true);
                }}
                onFilterClicked={() => {
                  viewModel.onFilterClicked();
                }}
                filteringModel={viewModel.truckFilteringModel}
                setFilteringModel={(model) =>
                  viewModel.setTruckFilteringModel(model)
                }
              />
            ) : popoverViewType == PopoverViewType.CHANGE_PASSWORD ? (
              <ChangePasswordView
                handleClosePopover={() => setAnchorElPosition(null)}
              />
            ) : (
              <></>
            )}
          </>
        }
      />
      <Container>
        <Stack pt={2} pb={2} spacing={3} width={"100%"} overflow={"hidden"}>
          <Stack
            width={"100%"}
            alignItems={"center"}
            justifyContent={"space-between"}
            direction={"row"}
            spacing={2}
          >
            <CustomTextView
              text="Home"
              fontWeight="bold"
              variant="h3"
              textColor={COLOR_MAIN}
            />
            <Stack direction={"row"} spacing={1}>
              <Tooltip title="Change Password">
                <IconButton
                  sx={{ border: "solid 1px " + COLOR_MAIN }}
                  onClick={(e) => {
                    setPopoverViewType(PopoverViewType.CHANGE_PASSWORD);
                    setAnchorElPosition({
                      top: e.clientY,
                      left: e.clientX,
                    });
                  }}
                >
                  <LockRounded sx={{ color: COLOR_MAIN }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Download Selected Content">
                <IconButton
                  sx={{ border: "solid 1px " + COLOR_MAIN }}
                  onClick={() => {
                    viewModel.generateXmlFile();
                  }}
                  disabled={
                    viewModel.trucks?.filter(
                      (t) => t.selected == true || t.selected == undefined
                    ).length == 0
                  }
                >
                  <DownloadRounded
                    sx={{
                      color:
                        viewModel.trucks?.filter(
                          (t) => t.selected == true || t.selected == undefined
                        ).length == 0
                          ? COLOR_LIGHT_GRAY
                          : COLOR_MAIN,
                    }}
                  />
                </IconButton>
              </Tooltip>
              <Tooltip title="Route Filtering">
                <IconButton
                  sx={{ border: "solid 1px " + COLOR_MAIN }}
                  onClick={(e) => {
                    setPopoverViewType(PopoverViewType.FILTER_TRUCKS);
                    setAnchorElPosition({
                      top: e.clientY,
                      left: e.clientX,
                    });
                  }}
                >
                  <SortRounded sx={{ color: COLOR_MAIN }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Logout">
                <IconButton
                  sx={{ border: "solid 1px " + COLOR_MAIN }}
                  onClick={() => {
                    SharedPreferences.clearByKey(STORAGE_KEYS.TOKEN);
                  }}
                >
                  <Logout sx={{ color: COLOR_MAIN }} />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>

          <Stack>
            <CustomTextView
              text="Imported trucks will be shown here, all sorted by the newest"
              variant="body1"
            />
            <CustomTextView
              text=" -Filter based on the Route or its date by clicking on the filter button on the top (3rd icon)."
              variant="body2"
              textColor={COLOR_LIGHT_GRAY}
            />
            <CustomTextView
              text=" -Type any route you want to export in a XML file, after selecting them all, click on the download button on the top."
              variant="body2"
              textColor={COLOR_LIGHT_GRAY}
            />
          </Stack>

          {viewModel.trucks?.length != 0 && (
            <CustomButtonView
              text="Select/Unselect All"
              onClick={() => viewModel.selectUnselectAll()}
            />
          )}

          <>
            {viewModel.isLoading ? (
              <LoadingView />
            ) : (
              <>
                {viewModel.trucks && (
                  <>
                    {viewModel.trucks.length === 0 ? (
                      <EmptyView />
                    ) : (
                      <Stack>
                        <Stack
                          height={"50vh"}
                          overflow={"scroll"}
                          border={"1px solid black"}
                          p={1}
                          borderRadius={4}
                          ref={trucksListRef}
                        >
                          <Grid container spacing={1}>
                            {viewModel.trucks.map((truck, index) => {
                              return (
                                <Grid size={12} key={truck._id}>
                                  <TrucksListItemView
                                    truck={truck}
                                    index={index}
                                    onChecked={(c, id) => {
                                      viewModel.onTruckChecked(c, id);
                                    }}
                                  />
                                </Grid>
                              );
                            })}
                          </Grid>
                        </Stack>
                        <Stack
                          pt={2}
                          width={"100%"}
                          alignItems={"center"}
                          justifyContent={"center"}
                        >
                          <Stack pb={1}>
                            {viewModel.trucks.length > 0 && (
                              <Button
                                variant="outlined"
                                sx={{
                                  borderRadius: 22,
                                  textTransform: "none",
                                  borderColor: COLOR_LESS_BLACK,
                                  "&:hover": {
                                    borderColor: COLOR_LESS_BLACK,
                                    background: COLOR_LIGHT_GRAY,
                                  },
                                }}
                                fullWidth
                                onClick={() => {
                                  if (trucksListRef.current)
                                    trucksListRef.current.scrollTop =
                                      trucksListRef.current.scrollHeight;
                                }}
                              >
                                <CustomTextView
                                  text={"Scroll to Bottom"}
                                  variant="h6"
                                  fontWeight="bold"
                                />
                              </Button>
                            )}
                          </Stack>

                          {viewModel.showLoadMoreLoading ? (
                            <LoadingView minHeight="5vh" />
                          ) : (
                            <>
                              {viewModel.showLoadMoreButton && (
                                <Button
                                  variant="outlined"
                                  sx={{
                                    borderRadius: 22,
                                    textTransform: "none",
                                    borderColor: COLOR_LESS_BLACK,
                                    "&:hover": {
                                      borderColor: COLOR_LESS_BLACK,
                                      background: COLOR_LIGHT_GRAY,
                                    },
                                  }}
                                  onClick={viewModel.getAllTrucks}
                                >
                                  <CustomTextView
                                    text={STRINGS_LOAD_MORE}
                                    variant="h6"
                                    fontWeight="bold"
                                  />
                                </Button>
                              )}
                            </>
                          )}
                        </Stack>
                      </Stack>
                    )}
                  </>
                )}
              </>
            )}
          </>
        </Stack>
      </Container>
    </Box>
  );
}

enum PopoverViewType {
  CHANGE_PASSWORD,
  FILTER_TRUCKS,
}
